import React, { useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography } from '@mui/material';
import EditorActions from '@/components/EditorActions.tsx';
import { useTranslation } from 'react-i18next';
import { ExpandMore, ViewList } from '@mui/icons-material';
import { useNotifications } from '@core/hooks/useNotifications';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { FormContainer } from '@/components';
import FormTextField from '@/components/form/FormTextField.tsx';
import { required } from '@core/utils/validation.ts';
import { ProductType } from '@core/store/slices/apiSlice.ts';

interface ProductTypeEditorProps {
    item: ProductType;
    onSave: (item: ProductType) => void;
    onCancel: () => void;
}

const RfidTagProductTypeEditor: React.FC<ProductTypeEditorProps> = ({ item, onSave, onCancel }) => {
    const [typeName, setTypeName] = useState(item?.typeName || '');
    const { t } = useTranslation();
    const { notifyWarning } = useNotifications();
    const [expanded, setExpanded] = React.useState(true);
    const [formValid, setFormValid] = React.useState(true);
    const formRef = useRef<FormContainerRef>(null);

    const handleSave = () => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return;
        }

        onSave({ ...item, typeName });
    };

    return (
        <Box className="editor-container">
            <Box className="editor-sidebar-actions">
                <EditorActions onSave={handleSave} onCancel={onCancel} saveDisabled={!typeName} />
            </Box>
            <Box className="editor-sidebar-content">
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">
                                <ViewList />
                                {t('productType')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2} xs={12}>
                                <Grid item xs={6} md={6}>
                                    <FormTextField
                                        name={'typeName'}
                                        label={t('name')}
                                        value={typeName}
                                        onChange={(e) => setTypeName(e.target.value)}
                                        required
                                        fullWidth
                                        rules={[required(t)]}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </FormContainer>
            </Box>
        </Box>
    );
};

export default RfidTagProductTypeEditor;
